<template>
  <div class="smw">
    <breadcrumb>
      <breadcrumb-item>
        <a href="#" @click.prevent="handleClose">SMS settings</a>
      </breadcrumb-item>
      <breadcrumb-item>
        <span>Set up SMS</span>
      </breadcrumb-item>
    </breadcrumb>

    <card class="smw__editor mb-0" card-body-classes="smw__card-body">
      <!-- Header row: title + status badge -->
      <div slot="header" class="smw__header">
        <div class="smw__header-title">
          <h4 class="text-white mb-0 smw__title">Set up SMS</h4>
          <div class="smw__header-meta">
            <badge :type="statusBadge.type">{{ statusBadge.label }}</badge>
            <small class="smw__edited">Step {{ activeStep + 1 }} of {{ steps.length }}</small>
          </div>
        </div>
      </div>

      <!-- Two-column inside: step nav left, content right -->
      <div class="smw__split">
        <aside class="smw__nav">
          <editor-section-nav
            :sections="navSections"
            :active-section="currentStep.key"
            @navigate="onNavNavigate"
          />
        </aside>

        <div class="smw__content">
          <p class="smw__lead">{{ currentStep.subtitle }}</p>
          <component
            :is="currentStep.component"
            :key="currentStep.key"
            :form-data="formData[currentStep.key]"
            :settings="stepSettings"
            :validation-errors="validationErrors"
            :search-results="numberSearchResults"
            :searching="searchingNumbers"
            :test-result="testResult"
            :sending-test="sendingTest"
            @update="handleStepUpdate(currentStep.key, $event)"
            @edit-profile="navigateToStep(0)"
            @search-numbers="onStepAction('search-numbers', $event)"
            @purchase-number="onStepAction('purchase-number', $event)"
            @send-test="$emit('send-test', $event)"
            @release-number="$emit('release-number')"
            @replace-number="$emit('replace-number')"
            @pause-sms="$emit('pause-sms')"
            @resume-sms="$emit('resume-sms')"
            @finish="$emit('finish')"
          />
        </div>
      </div>

      <!-- Footer action bar -->
      <div class="smw__footer">
        <span class="smw__footer-msg">{{ currentStep.title }}</span>
        <div class="smw__footer-actions">
          <base-button
            v-if="activeStep > 0"
            type="secondary"
            size="sm"
            :disabled="saving"
            @click="goBack"
          >
            Back
          </base-button>
          <!-- Hidden rather than disabled once SMS is live: there is nothing
               left to continue to, and a permanently greyed-out button reads
               as something being broken. -->
          <base-button v-if="!setupComplete" type="primary" size="sm" :disabled="continueDisabled" @click="handleContinue">
            {{ continueButtonLabel }}
          </base-button>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import { Badge } from 'src/components';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import BreadcrumbItem from '@/components/Breadcrumb/BreadcrumbItem';
import EditorSectionNav from '@/components/PaymentPages/EditorSectionNav';
import SMSOnboardingStepBusiness from './steps/SMSOnboardingStepBusiness';
import SMSOnboardingStepBrand from './steps/SMSOnboardingStepBrand';
import SMSOnboardingStepCampaign from './steps/SMSOnboardingStepCampaign';
import SMSOnboardingStepNumber from './steps/SMSOnboardingStepNumber';
import SMSOnboardingStepTest from './steps/SMSOnboardingStepTest';

// Preview switch: when true, locked steps become navigable and render their
// ready states with a synthesized status, with every action intercepted
// client-side (nothing reaches the backend). Used to review unbuilt-yet steps
// during development. MUST stay false in anything a tenant can reach.
const STEP_PREVIEW = false;
const PREVIEW_STATUS_BY_STEP = ['not_started', 'brand_pending', 'campaign_pending', 'number_pending', 'test_pending'];

const STATUS_TO_STEP = {
  not_started: 0,
  // profile_pending = saved but Twilio submission incomplete; still on Business
  profile_pending: 0,
  profile_review: 1,
  brand_pending: 1,
  brand_review: 1,
  brand_rejected: 1,
  campaign_pending: 2,
  campaign_review: 2,
  campaign_rejected: 2,
  number_pending: 3,
  test_pending: 4,
  active: 4,
  suspended: 4,
  cancelled: 4,
  paused: 4
};

export default {
  name: 'SMSOnboardingWizard',
  components: {
    Badge,
    Breadcrumb,
    BreadcrumbItem,
    EditorSectionNav,
    'sms-onboarding-step-business': SMSOnboardingStepBusiness,
    'sms-onboarding-step-brand': SMSOnboardingStepBrand,
    'sms-onboarding-step-campaign': SMSOnboardingStepCampaign,
    'sms-onboarding-step-number': SMSOnboardingStepNumber,
    'sms-onboarding-step-test': SMSOnboardingStepTest
  },
  props: {
    settings: {
      type: Object,
      default: null
    },
    // Company-settings values used to seed the business profile form on a
    // fresh onboarding. Saved profile values always win over the prefill.
    businessPrefill: {
      type: Object,
      default: null
    },
    saving: {
      type: Boolean,
      default: false
    },
    validationErrors: {
      type: Array,
      default: () => []
    },
    numberSearchResults: {
      type: Array,
      default: () => []
    },
    searchingNumbers: {
      type: Boolean,
      default: false
    },
    testResult: {
      type: Object,
      default: null
    },
    sendingTest: {
      type: Boolean,
      default: false
    }
  },
  data() {
    // Seed the business form in data(), not mounted(): step components
    // snapshot their form-data prop at creation, and children mount BEFORE
    // the parent's mounted hook, so a mounted-time merge arrives too late.
    // Seed order: company-settings prefill first, saved profile on top.
    const initialBusiness = {
      ...(this.businessPrefill || {}),
      ...(this.settings?.business_profile || {})
    };
    return {
      activeStep: 0,
      steps: [
        {
          key: 'business',
          title: 'Business profile',
          subtitle: 'Tell us about your business so carriers can verify you are a legitimate sender.',
          component: 'sms-onboarding-step-business'
        },
        {
          key: 'brand',
          title: 'Brand registration',
          subtitle: 'Submit your business brand to the A2P 10DLC registry for carrier approval.',
          component: 'sms-onboarding-step-brand'
        },
        {
          key: 'campaign',
          title: 'Campaign registration',
          subtitle: 'Register the types of messages you will send and how customers opt in.',
          component: 'sms-onboarding-step-campaign'
        },
        {
          key: 'number',
          title: 'Phone number',
          subtitle: 'Choose a local 10DLC number that customers will see on every message.',
          component: 'sms-onboarding-step-number'
        },
        {
          key: 'test',
          title: 'Test and activate',
          subtitle: 'Send a test message to confirm everything works, then turn on SMS.',
          component: 'sms-onboarding-step-test'
        }
      ],
      formData: {
        business: initialBusiness,
        brand: {},
        campaign: {},
        number: {},
        test: {}
      }
    };
  },
  computed: {
    currentStep() {
      return this.steps[this.activeStep];
    },
    isLastStep() {
      return this.activeStep === this.steps.length - 1;
    },
    serverStep() {
      const status = this.settings?.registration_status || 'not_started';
      return STATUS_TO_STEP[status] ?? 0;
    },
    // Settings handed to the current step. In preview mode, steps beyond the
    // real server progress get a synthesized ready-state status; everything
    // at or before real progress always sees the truth.
    stepSettings() {
      if (!STEP_PREVIEW || this.activeStep <= this.serverStep) return this.settings;
      return {
        ...(this.settings || {}),
        registration_status: PREVIEW_STATUS_BY_STEP[this.activeStep]
      };
    },
    isPreviewingStep() {
      return STEP_PREVIEW && this.activeStep > this.serverStep;
    },
    // Statuses in which the business profile is editable; mirrors the
    // backend's PROFILE_EDITABLE_STATUSES guard so the UI never offers a
    // submit the server will 409.
    businessEditable() {
      const status = this.settings?.registration_status || 'not_started';
      return ['not_started', 'profile_pending', 'brand_rejected'].includes(status);
    },
    continueButtonLabel() {
      if (this.saving) return 'Submitting...';
      const status = this.stepSettings?.registration_status || 'not_started';
      // Viewing an already-completed step: Continue is plain forward nav.
      if (this.activeStep < this.serverStep) return 'Next';
      if (this.currentStep.key === 'business') {
        if (status === 'brand_rejected') return 'Save business details';
        if (!this.businessEditable) return 'Profile submitted';
        return 'Continue';
      }
      if (this.currentStep.key === 'brand') {
        if (status === 'profile_review') return 'Waiting on profile approval';
        if (status === 'brand_review') return 'Brand in review';
        if (status === 'brand_rejected') return 'Resubmit brand registration';
        return 'Submit brand registration';
      }
      if (this.currentStep.key === 'campaign') {
        if (status === 'campaign_review') return 'Campaign in review';
        if (status === 'campaign_rejected') return 'Resubmit campaign';
        return 'Submit campaign';
      }
      if (this.currentStep.key === 'number') {
        return 'Select a number above';
      }
      if (this.currentStep.key === 'test') {
        if (status === 'paused') return 'SMS paused';
        if (status === 'active' || status === 'suspended') return 'SMS active';
        return 'Send test and activate';
      }
      return 'Continue';
    },
    navSections() {
      const status = this.settings?.registration_status || 'not_started';
      return this.steps.map((step, i) => ({
        id: step.key,
        num: i + 1,
        label: step.title,
        sublabel: this.stepStatusLabel(i),
        done: i < this.serverStep,
        disabled: STEP_PREVIEW ? false : i > this.serverStep,
        warn: i === this.serverStep && status.endsWith('_rejected'),
        warnText: 'Action needed'
      }));
    },
    // SMS is live and the tenant is on the final step, so the wizard is now a
    // read-only record of what was submitted rather than a flow to advance.
    setupComplete() {
      const status = this.stepSettings?.registration_status || 'not_started';
      return ['active', 'suspended', 'cancelled', 'paused'].includes(status) && this.currentStep.key === 'test';
    },
    continueDisabled() {
      if (this.saving) return true;
      const status = this.stepSettings?.registration_status || 'not_started';
      // Forward nav through completed steps is always allowed.
      if (this.activeStep < this.serverStep) return false;
      if (this.currentStep.key === 'business') {
        // Profile locked past submission; the server would 409 the edit.
        return !this.businessEditable;
      }
      if (this.currentStep.key === 'brand') {
        return status === 'profile_review' || status === 'brand_review';
      }
      if (this.currentStep.key === 'campaign') {
        if (status === 'campaign_review') return true;
        // The tenant must affirm the described opt-in flow is really what their
        // customers see. Campaign review is manual, and a description that does
        // not match the live booking page is the rejection we keep hitting.
        return !(this.formData.campaign && this.formData.campaign.attested);
      }
      if (this.currentStep.key === 'number') {
        // Purchase happens in-step via Select; the footer button is inert.
        return true;
      }
      if (this.currentStep.key === 'test') {
        return status === 'active' || status === 'suspended' || status === 'paused';
      }
      return false;
    },
    statusBadge() {
      const status = this.settings?.registration_status || 'not_started';
      if (status === 'active') return { type: 'success', label: 'active' };
      if (status === 'suspended') return { type: 'warning', label: 'suspended' };
      if (status === 'cancelled') return { type: 'default', label: 'cancelled' };
      if (status === 'paused') return { type: 'warning', label: 'paused' };
      if (status.endsWith('_rejected')) return { type: 'danger', label: 'action needed' };
      if (status.endsWith('_review')) return { type: 'info', label: 'in review' };
      return { type: 'default', label: 'in progress' };
    }
  },
  watch: {
    'settings.registration_status'(newStatus, oldStatus) {
      if (newStatus === oldStatus) return;
      const newServerStep = STATUS_TO_STEP[newStatus] ?? 0;
      if (newServerStep > this.activeStep) {
        this.activeStep = newServerStep;
      } else if (newStatus === 'number_pending' && newServerStep < this.activeStep) {
        // Replace number: the only move backwards, from the test step to the number step
        this.activeStep = newServerStep;
      }
    },
    'settings.business_profile': {
      deep: true,
      handler(newProfile) {
        if (newProfile && Object.keys(newProfile).length > 0) {
          this.formData.business = { ...this.formData.business, ...newProfile };
        }
      }
    }
  },
  mounted() {
    this.activeStep = this.serverStep;
  },
  methods: {
    stepStatusLabel(index) {
      const status = this.settings?.registration_status || 'not_started';
      if (index < this.serverStep) return 'Complete';
      if (index === this.serverStep) {
        if (status.endsWith('_review')) return 'In review';
        if (status.endsWith('_rejected')) return 'Action needed';
        return 'In progress';
      }
      return 'Not started';
    },
    navigateToStep(index) {
      if (STEP_PREVIEW || index <= this.serverStep) {
        this.activeStep = index;
      }
    },
    onStepAction(eventName, payload) {
      if (this.isPreviewingStep) {
        this.previewNotify();
        return;
      }
      this.$emit(eventName, payload);
    },
    previewNotify() {
      this.$notify({
        type: 'info',
        message: 'Preview only. This step unlocks when the earlier steps complete.',
        icon: 'tim-icons icon-bell-55'
      });
    },
    onNavNavigate(sectionId) {
      const index = this.steps.findIndex((s) => s.key === sectionId);
      if (index !== -1) this.navigateToStep(index);
    },
    handleStepUpdate(sectionKey, data) {
      this.formData[sectionKey] = { ...this.formData[sectionKey], ...data };
    },
    handleContinue() {
      if (this.isPreviewingStep) {
        this.previewNotify();
        return;
      }
      // Viewing an already-completed step: plain forward navigation, never
      // past the server's step.
      if (this.activeStep < this.serverStep) {
        this.activeStep += 1;
        return;
      }

      const stepKey = this.currentStep.key;
      if (stepKey === 'business') {
        this.$emit('submit-business-profile', { ...this.formData.business });
        return;
      }
      if (stepKey === 'brand') {
        this.$emit('submit-brand');
        return;
      }
      if (stepKey === 'campaign') {
        const c = this.formData.campaign;
        this.$emit('submit-campaign', {
          use_case: c.use_case,
          opt_in_description: c.opt_in_description,
          privacy_policy_url: c.privacy_policy_url,
          terms_url: c.terms_url,
          opt_in_evidence_url: c.opt_in_evidence_url,
          // `attested` is deliberately not sent: it gates our own Submit button,
          // it is not a registry field.
          sample_messages: [
            c.sample_message_1,
            c.sample_message_2,
            c.sample_message_3,
            c.sample_message_4,
            c.sample_message_5
          ].filter(Boolean)
        });
        return;
      }
      if (stepKey === 'test') {
        const phone = (this.formData.test.test_phone || '').trim();
        if (!phone) {
          this.$notify({
            type: 'warning',
            message: 'Enter the phone number to send the test to',
            icon: 'tim-icons icon-alert-circle-exc'
          });
          return;
        }
        this.$emit('activate', { test_phone: phone });
      }
    },
    goBack() {
      if (this.activeStep > 0) {
        this.activeStep -= 1;
      }
    },
    handleClose() {
      this.$emit('cancel');
    }
  }
};
</script>

<style lang="scss" scoped>
// Shell mirrors the PaymentPageEditor editor-card pattern (breadcrumb, single
// card, nav/content split, footer action bar) so multi-section editors read
// the same across the app.
.smw {
  // ─── Card header ─────────────────────────────────────────────────────
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  &__header-title { flex: 1; min-width: 0; }
  &__title {
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.2;
  }
  &__header-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 8px;
  }
  &__edited {
    color: rgba(255, 255, 255, 0.5);
    font-size: 11.5px;
    margin-left: 4px;
  }

  // ─── Two-column split (nav + content) ────────────────────────────────
  &__split {
    display: flex;
    align-items: stretch;
    min-height: 480px;

    @media (max-width: 991px) {
      flex-direction: column;
      min-height: 0;
    }
  }

  &__nav {
    width: 220px;
    flex-shrink: 0;
    padding-right: 16px;
    border-right: 1px solid rgba(125, 125, 125, 0.15);

    @media (max-width: 991px) {
      width: 100%;
      padding-right: 0;
      padding-bottom: 12px;
      border-right: none;
      border-bottom: 1px solid rgba(125, 125, 125, 0.15);
      margin-bottom: 16px;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
    padding-left: 24px;

    @media (max-width: 991px) {
      padding-left: 0;
    }
  }

  &__lead {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.5;
    margin: 0 0 16px;
  }

  // ─── Footer action bar ───────────────────────────────────────────────
  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid rgba(125, 125, 125, 0.15);
    gap: 12px;
    flex-wrap: wrap;
  }
  &__footer-msg {
    font-size: 12.5px;
    color: rgba(255, 255, 255, 0.6);
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  &__footer-actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  // When the footer wraps on narrow screens, keep the actions pinned right
  // instead of space-between dropping the lone actions row to the left.
  @media (max-width: 480px) {
    &__footer {
      justify-content: flex-end;
    }
    &__footer-msg {
      flex: 1 1 100%;
    }
  }
}

// Trim padding inside the card body since we manage our own spacing
:deep(.smw__card-body) {
  padding: 20px;

  @media (max-width: 575px) {
    padding: 14px;
  }
}
</style>

<style>
.white-content .smw__edited { color: rgba(0, 0, 0, 0.5); }
.white-content .smw__footer-msg { color: rgba(0, 0, 0, 0.6); }
.white-content .smw__lead { color: rgba(0, 0, 0, 0.6); }
</style>
